import type { ReactElement } from 'react'
import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { SignInModal } from '../components'
import { useAuth } from '../hooks'
import { LandingNav } from './landing/LandingNav'
import { LandingHero } from './landing/LandingHero'
import { LandingCards } from './landing/LandingCards'
import { CloudWash } from './landing/CloudWash'
import {
  PhilosophySection,
  StatementSection,
  CtaSection,
  FooterSection,
} from './landing/LandingContent'
import '../styles/landing-page.css'

export interface LandingPageProps {
  onLearnMore?: (() => void) | undefined
  onAbout?: (() => void) | undefined
  onPricing?: (() => void) | undefined
}

type LandingTheme = 'light' | 'dark'

const STORAGE_KEY = 'evidenx-landing-theme'

function readStoredTheme(): LandingTheme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark') return 'dark'
  } catch {
    void 0
  }
  return 'light'
}

export function LandingPage({ onLearnMore, onAbout, onPricing }: LandingPageProps): ReactElement {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [isSignInOpen, setIsSignInOpen] = useState(false)
  const [theme, setTheme] = useState<LandingTheme>(readStoredTheme)

  const handleToggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === 'light' ? 'dark' : 'light'
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        void 0
      }
      return next
    })
  }, [])

  const handleSignInClick = useCallback(() => {
    if (isAuthenticated) {
      void navigate('/dashboard')
    } else {
      setIsSignInOpen(true)
    }
  }, [isAuthenticated, navigate])

  const handleSignInSuccess = useCallback(() => {
    setIsSignInOpen(false)
    void navigate('/dashboard')
  }, [navigate])

  return (
    <>
      <div className="landing-page" data-theme={theme}>
        <div className="lp-grain" />
        <CloudWash />
        <LandingNav
          theme={theme}
          onToggleTheme={handleToggleTheme}
          onSignIn={handleSignInClick}
          onAbout={onAbout}
          onPricing={onPricing}
          isAuthenticated={isAuthenticated}
        />
        <LandingHero />
        <PhilosophySection />
        <LandingCards />
        <StatementSection />
        <CtaSection onSignIn={handleSignInClick} onLearnMore={onLearnMore} />
        <FooterSection />
      </div>
      <SignInModal
        isOpen={isSignInOpen}
        onClose={() => {
          setIsSignInOpen(false)
        }}
        onSuccess={handleSignInSuccess}
      />
    </>
  )
}

export default LandingPage
